import { Base64 } from 'js-base64';
import { buildTinkWaitingSearch } from 'utils/tinkReports';
import { getFetchHeaders } from 'utils/fetchHeaders';
import { redirection401ToLogin, useGetClientApplication } from 'api';
import { ROUTES } from 'utils/constants/constants_routes';
import { SESSION_TINK_REASON } from 'utils/constants';
import { showNotification } from 'components/GlobalNotification/GlobalNotification';
import { TinkMessageEvent } from 'components/Tink/parseTinkMessageEvent';
import { useCallback, useEffect, useRef, useState, useTransition } from 'react';
import { useCustomNavigation } from 'hooks/useCustomNavigation';
import { useErrorManager } from 'InternalLibrary';
import { useLocation, useNavigate } from 'react-router-dom';
import smartlookClient from 'smartlook-client';
import TinkEvent from 'hooks/eventCollector/events/TinkEvent';
import useClient from 'hooks/useClient';
import useEventCollector from 'hooks/eventCollector/useEventCollector';
import useFirebase from 'hooks/useFirebase';
import useTink from 'hooks/useTink';

const SESSION_TINK_SESSION_KEY = 'tink_application_state';

export function useOnlineBankVerification() {
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [loading, startLoading] = useTransition();
  const [isModalOpened, setIsModalOpened] = useState(false);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [isTinkCloseModalOpen, setIsTinkCloseModalOpen] = useState(false);
  const [
    isTinkCredentialsFailedModalOpen,
    setIsTinkCredentialsFailedModalOpen,
  ] = useState(false);
  const credentialsValidationFailedCountRef = useRef(0);

  const navigate = useNavigate();
  const apiClientApplication = useGetClientApplication();
  const { client, fetchClient } = useClient();
  const {
    getLink,
    sendReportIds,
    sendSmartlookEvent,
    isTinkRedirectionEnabled,
  } = useTink();
  const { setError } = useErrorManager();
  const { firebaseData } = useFirebase();
  const { track } = useEventCollector();
  const location = useLocation();
  const { onlineBankVerifyRoute } = useCustomNavigation();

  const [applicationIdAndCompleted, setApplicationIdAndCompleted] = useState<{
    id: number | undefined;
    completed: boolean | undefined;
  }>(() => {
    try {
      const stored = sessionStorage.getItem(SESSION_TINK_SESSION_KEY);
      return stored
        ? JSON.parse(stored)
        : {
            id: apiClientApplication.data?.id,
            completed: apiClientApplication.data?.completed,
          };
    } catch {
      return { id: undefined, completed: undefined };
    }
  });

  const setTinkAppState = (value: {
    id: number | undefined;
    completed: boolean | undefined;
  }) => {
    sessionStorage.setItem(SESSION_TINK_SESSION_KEY, JSON.stringify(value));
    setApplicationIdAndCompleted(value);
  };

  const tinkEventRedirectionInProgressRef = useRef(false);
  const isTinkRedirectionEnabledRef = useRef(isTinkRedirectionEnabled);
  isTinkRedirectionEnabledRef.current = isTinkRedirectionEnabled;

  const toggleVideoModal = useCallback(() => {
    setIsVideoOpen((prevState) => !prevState);
  }, []);

  const startTinkFlow = useCallback(() => {
    const enabled = isTinkRedirectionEnabledRef.current;
    if (enabled) {
      const link = getLink();
      smartlookClient.track('TINK_REDIRECT_STARTED', { url: link.toString() });
      window.location.assign(link);
    } else setDialogOpen(true);
  }, [getLink]);

  useEffect(() => {
    const fetchMissing = async () => {
      if (!applicationIdAndCompleted.id) {
        try {
          const fetchedApplication = await apiClientApplication.fetch({
            headers: getFetchHeaders({
              Accept: 'application/vnd.4finance.web.v1.hal+json',
              'Content-Type': 'application/vnd.4finance.web.v1.hal+json',
            }),
          });
          if (fetchedApplication?.resolution === 'REJECTED') {
            navigate(ROUTES.FIRST_LOAN_REJECTED);
            return;
          }

          await fetchClient();

          setTinkAppState({
            id: fetchedApplication?.id,
            completed: fetchedApplication?.completed,
          });
        } catch (error) {
          if (error.status === 401) {
            redirection401ToLogin();
          }
        }
      }
    };

    fetchMissing();
  }, []);

  const propagateTinkToBESmartlookEventCollector = async (
    payload: TinkMessageEvent,
  ) => {
    const { data, type, error } = payload;

    if (type !== 'status') {
      if (type === 'error') {
        try {
          await sendReportIds({ type, ...error });
        } catch (error) {
          if (error.status === 401) {
            redirection401ToLogin();
          }
        }
      }

      if (data) sendSmartlookEvent(data);
    }
  };

  const redirectionFromTinkEvent = useCallback(
    (path: string) => {
      if (tinkEventRedirectionInProgressRef.current) {
        return;
      }
      tinkEventRedirectionInProgressRef.current = true;
      navigate(path);
    },
    [navigate],
  );

  const handleTinkError = (error: {
    status: string | null;
    trackingId: string | null;
    reason: string | null;
  }) => {
    switch (error.status) {
      case 'USER_CANCELLED': {
        if (!isTinkRedirectionEnabled) setDialogOpen(false);
        setIsTinkCloseModalOpen(true);
        return;
      }
      case 'BAD_REQUEST': // Note: Seems Tink doesn't actually fires this error status.
      case 'INTERNAL_ERROR':
      case 'TEMPORARY_ERROR': {
        if (!isTinkRedirectionEnabled) setDialogOpen(false);
        setError(error as any); // error from tink (3rd party)
        const queryParams = new URLSearchParams({
          trackingId: error.trackingId ?? '',
          reason: error.reason ?? '',
          status: error.status ?? '',
          tinkReportFailed: 'true',
        });

        sessionStorage.setItem(
          SESSION_TINK_REASON,
          Base64.encode(error.status),
        );

        return redirectionFromTinkEvent(
          `${ROUTES.FIRST_LOAN_ONLINE_BANK_ADD_DOCUMENTS}?${queryParams}`,
        );
      }
      case 'AUTHENTICATION_ERROR': {
        if (!isTinkRedirectionEnabled) setDialogOpen(false);
        setError(error as any); // error from tink (3rd party)
        const queryParams = new URLSearchParams({
          trackingId: error.trackingId ?? '',
          reason: error.reason ?? '',
          status: error.status ?? '',
        });

        return redirectionFromTinkEvent(
          `${onlineBankVerifyRoute}?${queryParams}`,
        );
      }
      default:
        break;
    }
  };

  const handleTinkReport = async (data: {
    transaction_report_id: string | undefined | null;
    risk_insights_report_id: string | undefined | null;
    reportGenerationId: string | undefined | null;
  }) => {
    let loanApplicationId = applicationIdAndCompleted?.id;

    if (!loanApplicationId) {
      try {
        loanApplicationId = (await apiClientApplication.fetch())?.id;
      } catch {
        // Silent error for now. testing new tink async mode
      }
    }

    const tinkDataToPropagate = {
      transactionReportId: data?.transaction_report_id || '',
      riskInsightsId: data?.risk_insights_report_id || '',
      reportGenerationId: data?.reportGenerationId || '',
    };

    smartlookClient.track('CHECK_TINK_QUERY_PARAMS', {
      isTinkFinished: Boolean(
        data?.transaction_report_id ||
          data?.risk_insights_report_id ||
          data?.reportGenerationId,
      ),
      reportGenearionId: data?.reportGenerationId || '',
      loanApplicationId: `${loanApplicationId}`,
    });

    showNotification(':');
    await sendReportIds({
      loanApplicationId,
      ...tinkDataToPropagate,
    });
    showNotification('.');
  };

  const handleTinkEvent = useCallback(
    async (payload: TinkMessageEvent) => {
      const { data, type, error } = payload;

      propagateTinkToBESmartlookEventCollector(payload);

      if (type !== 'status') {
        track(
          new TinkEvent({
            data,
            typeTink: type,
            error,
          }),
        );

        if (type === 'application-event' && Boolean(data.event)) {
          smartlookClient.track(`TINK_${data.event}`, {});

          if (data.event === 'CREDENTIALS_VALIDATION_FAILED') {
            credentialsValidationFailedCountRef.current += 1;
            if (credentialsValidationFailedCountRef.current >= 3) {
              setIsTinkCredentialsFailedModalOpen(true);
            }
            return;
          }
        }
      }

      if (type === 'error') {
        handleTinkError(error);
      } else if (type === 'view-event' && data?.screen === 'ERROR_SCREEN') {
        setDialogOpen(false);
        return redirectionFromTinkEvent(onlineBankVerifyRoute);
      } else if (type === 'reports') {
        if (firebaseData.REGISTRATION_TINK_IFRAME_WAITING_SCREEN) {
          navigate({
            pathname: ROUTES.FIRST_LOAN_ONLINE_BANK_TINK_WAITING,
            search: buildTinkWaitingSearch({
              reports_generation_job_id: data?.reportGenerationId ?? null,
              transaction_report_id: data?.transaction_report_id ?? null,
              risk_insights_report_id: data?.risk_insights_report_id ?? null,
            }),
          });
        } else {
          await handleTinkReport(data).catch(() => {});
          return redirectionFromTinkEvent(ROUTES.FIRST_LOAN_ONLINE_BANK_CHECK);
        }
      }
    },
    [track, redirectionFromTinkEvent, setError, applicationIdAndCompleted.id],
  );

  const onVerifyHandler = async () => {
    if (applicationIdAndCompleted.completed !== true) {
      try {
        const fetchedApplication = await apiClientApplication.fetch({
          headers: getFetchHeaders({
            Accept: 'application/vnd.4finance.web.v1.hal+json',
            'Content-Type': 'application/vnd.4finance.web.v1.hal+json',
          }),
        });
        if (fetchedApplication?.resolution === 'REJECTED') {
          navigate(ROUTES.FIRST_LOAN_REJECTED);
        }
      } catch (error) {
        if (error.status === 401) {
          redirection401ToLogin();
        }
      }
    }
    startTinkFlow();
  };

  const onSubmit = async () => {
    startLoading(async () => {
      await onVerifyHandler();
    });
  };

  const onRetryTink = useCallback(() => {
    setIsTinkCloseModalOpen(false);
    startTinkFlow();
  }, [startTinkFlow]);

  // When TinkWaiting redirects back here with an error, show the close modal.
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    if (searchParams.get('error_reason') === 'REQUEST_FAILED_CREATE_REPORT') {
      setError('default'); // error from 3rd party - tink
      navigate(
        `${ROUTES.FIRST_LOAN_ONLINE_BANK_ADD_DOCUMENTS}?${searchParams}`,
      );
      // eslint-disable-next-line no-console
      console.error(
        `Tink error. Search params: ${decodeURIComponent(location.search)}`,
      );
      return;
    }

    const errorParam = searchParams.get('error');

    if (errorParam) {
      if (errorParam !== 'USER_CANCELLED') {
        navigate(`${ROUTES.FIRST_LOAN_ONLINE_BANK_ERROR}?${searchParams}`);
        // eslint-disable-next-line no-console
        console.error(
          `Tink error. Search params: ${decodeURIComponent(location.search)}`,
        );
        return;
      } else {
        setIsTinkCloseModalOpen(true);
      }
    }
  }, [location.search]);

  return {
    isDialogOpen,
    isModalOpened,
    setIsModalOpened,
    isVideoOpen,
    loading,
    toggleVideoModal,
    getLink,
    handleTinkEvent,
    onSubmit,
    isTinkCloseModalOpen,
    isTinkCredentialsFailedModalOpen,
    setIsTinkCredentialsFailedModalOpen,
    onRetryTink,
    handleTinkError,
    handleTinkReport,
    isReadyToSubmit: Boolean(client) && Boolean(applicationIdAndCompleted.id),
  };
}
